import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Repeat } from 'lucide-react'
import {
  useAddItem, useAddList, useArchiveList, usePinList, useRemoveItem, useRenameList,
  useReorderItems, useSetItemDone, useSetItemFields, useUpdateItem, useBumpUsual,
  useCompleteTask, useClearDone, useMoveLine, tickTask,
} from '../../lib/mutations'
import { useListItems, useListUsuals, useLists } from '../../lib/queries'
import { attempt, useWriteError } from '../../lib/useAction'
import { aisleOf, needsLookup, AISLES } from '../../lib/aisles'
import { requestAisleLookup, useAisleDict, useGroceryAisles } from '../../lib/groceryAisles'
import { parseQty } from '../../lib/listParse'
import { addTask, parseTask } from '../../lib/listAdd'
import { hereCurrency, priceSymbol } from '../../lib/currency'
import { berlinDay, dayStart } from '../../lib/today'
import { KIND_OPTIONS, kindAddsToName } from '../../lib/listKinds'
import { go, replace, useRoute } from '../../lib/router'
import { pickDeskList, SAVED_LIST } from '../../lib/deskList'
import { canClear, dueLabel, foldTasks, LINGER_MS, pinnedByDue, primaryTaskList, shortDay } from '../../lib/taskRules'
import { repeatLabel } from '../../lib/repeatParse'
import { dropped, droppedPhrase, movePatch } from '../../lib/moveLine'
import { deskKeyAction } from '../../lib/deskKeys'
import { taskFromQuery } from '../../lib/taskLink'
import { LEAVE_MS } from '../../ui/Row'
import { usualsToOffer } from '../../lib/usuals'
import { finishedHead, foldFinished, isFinished } from '../../lib/finished'
import { purchaseStatusOf } from '../../lib/listSummary'
import { stepIn } from '../../lib/reorder'
import { useToast } from '../../ui'
import { toastRemovedLine } from '../undo'
import { Failed } from '../../screens/Stub'
import { Shortlist } from '../../screens/Shortlist'
import type { List, ListItem, ListKind, PurchaseStatus } from '../../lib/types'
import { userKey } from '../../lib/userStorage'
import { topPosition } from '../../lib/newPosition'
import '../lists.css'

const KIND_LABEL: Record<ListKind, string> = Object.fromEntries(
  KIND_OPTIONS.map(o => [o.value, o.label]),
) as Record<ListKind, string>

const PLACEHOLDER: Record<ListKind, string> = {
  grocery: 'Add — “200g emmentaler”, “2 tortillas”',
  task: 'Add a task — “Renew passport by Friday”',
  buy: 'Add something to buy',
  collection: 'Add to the collection',
  template: 'Add a step',
}

const STATUSES: PurchaseStatus[] = ['considering', 'ordered', 'bought']

const OPEN_KEY = 'kiez-desk-list'
const readOpen = () => { try { return localStorage.getItem(userKey(OPEN_KEY)) } catch { return null } }
const writeOpen = (id: string) => { try { localStorage.setItem(userKey(OPEN_KEY), id) } catch { /* private window */ } }

/** What "open" means per kind — `lib/finished.ts`, the one rule both
 *  shells draw from (18 Oct). A list whose kind is not known yet is open. */
const isClosed = (i: ListItem, kind: ListKind | undefined) => !!kind && isFinished(kind, i)

const byPosition = (a: ListItem, b: ListItem) => a.position - b.position

/** As many usual chips as the phone's grocery list offers. */
const MAX_USUALS = 12

const euros = (cents: number | null) => (cents == null ? '' : (cents / 100).toFixed(cents % 100 ? 2 : 0))
const toCents = (text: string): number | null => {
  const n = Number(text.replace(/[€$£\s]/g, '').replace(',', '.'))
  return text.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null
}

export function Lists() {
  const listsQ = useLists()
  const itemsQ = useListItems()
  const lists = listsQ.data ?? []
  const items = itemsQ.data ?? []
  const routeId = useRoute().id
  const [remembered, setRemembered] = useState<string | null>(readOpen)
  const [creating, setCreating] = useState(false)
  // Held here, not in the fold: unarchiving the last archived list empties
  // the fold, which unmounts, taking the mutation and its error with it; the
  // rollback then mounts a fresh, closed fold that never heard of it (a4-06).
  const unarchiveM = useArchiveList()
  const unarchiveError = useWriteError(unarchiveM)
  // The created row arrives with the lists query's refetch; open it by name then.
  const [want, setWant] = useState<string | null>(null)

  const active = useMemo(
    () => lists.filter(l => !l.archived_at)
      .sort((a, b) => Number(b.pinned) - Number(a.pinned) || a.position - b.position),
    [lists],
  )
  const archived = useMemo(() => lists.filter(l => l.archived_at), [lists])
  const openCount = useMemo(() => {
    const kindOf = new Map(lists.map(l => [l.id, l.kind]))
    const m = new Map<string, number>()
    for (const i of items) if (!isClosed(i, kindOf.get(i.list_id))) m.set(i.list_id, (m.get(i.list_id) ?? 0) + 1)
    return m
  }, [items, lists])

  // The route says what is on screen; the stored id answers only a bare
  // `#/lists`. A route or stored id naming a list since deleted or archived
  // falls to the first list rather than an empty pane (lib/deskList.ts).
  const pick = pickDeskList(routeId, remembered, active.map(l => l.id), !!listsQ.data)
  const open = pick.open === SAVED_LIST ? null : active.find(l => l.id === pick.open) ?? null
  const show = (id: string) => go('lists', id)
  useEffect(() => {
    if (pick.remember && pick.open && pick.open !== remembered) { writeOpen(pick.open); setRemembered(pick.open) }
    // Replace, not push: the bad link is not a place to come back to.
    if (pick.rewrite) { history.replaceState(null, '', '#/lists'); window.dispatchEvent(new HashChangeEvent('hashchange')) }
  }, [pick.open, pick.remember, pick.rewrite, remembered])
  useEffect(() => {
    if (!want) return
    const made = [...active].reverse().find(l => l.name === want)
    if (made) { show(made.id); setWant(null) }
  }, [want, active])
  // Tasks plan D9: `#/lists?task=<text>` adds a task to the first task list
  // and opens it, the way `?recipe=` opens the grocery sheet on the phone.
  // Read once, the hash rewritten without it (a reload adds nothing twice),
  // and written only once the lists have arrived.
  const route = useRoute()
  const linkAddM = useAddItem()
  const linkToast = useToast()
  const [linkTask, setLinkTask] = useState<string | null>(null)
  const queryTask = taskFromQuery(route.query)
  useEffect(() => {
    if (!queryTask) return
    setLinkTask(queryTask)
    replace('lists', routeId)
  }, [queryTask, routeId])
  useEffect(() => {
    if (!linkTask || !listsQ.data) return
    const text = linkTask
    setLinkTask(null)
    const target = primaryTaskList(listsQ.data)
    if (!target) { linkToast({ text: 'No task list yet' }); return }
    const rows = (itemsQ.data ?? []).filter(i => i.list_id === target.id)
    void addTask({ addM: linkAddM }, target, rows, text).then(out => {
      if (!out.ok || out.empty) return
      const p = parseTask(text)
      linkToast({ text: `Added "${p.body}"${p.due_on ? ` · due ${shortDay(p.due_on)}` : ''}` })
      go('lists', target.id)
    })
  }, [linkTask, listsQ.data, itemsQ.data, linkAddM, linkToast])

  const pending = listsQ.isPending || itemsQ.isPending
  // A read that failed with nothing cached to show: say so ahead of the
  // empty state, which would otherwise read as the truth (a4-07). A failed
  // refetch over cached rows keeps the rows.
  const readError = (!listsQ.data && listsQ.error) || (!itemsQ.data && itemsQ.error) || null

  return (
    <div className="lp">
      <nav className="lp-nav" aria-label="Lists">
        <button
          type="button" aria-current={pick.open === SAVED_LIST ? 'true' : undefined}
          onClick={() => show(SAVED_LIST)}
        >
          <span className="lp-nav__name">
            {/* The phone's name for it, and the pane's own heading. */}
            <span>Shortlist</span>
            <span className="lp-nav__kind">Events you saved</span>
          </span>
        </button>
        {active.map(l => (
          <button
            key={l.id} type="button" aria-current={open?.id === l.id ? 'true' : undefined}
            onClick={() => show(l.id)}
          >
            <span className="lp-nav__name">
              <span>{l.name}{l.pinned && <span className="lp-nav__pin" role="img" aria-label="Pinned" title="Pinned">●</span>}</span>
              {kindAddsToName(l.name, KIND_LABEL[l.kind]) && <span className="lp-nav__kind">{KIND_LABEL[l.kind]}</span>}
            </span>
            <span className="lp-nav__n">{openCount.get(l.id) ?? 0}</span>
          </button>
        ))}
        {creating
          ? <NewList onDone={name => { setCreating(false); setWant(name) }} />
          : <button type="button" className="lp-nav__new" onClick={() => setCreating(true)}>+ New list</button>}
        {archived.length > 0 && <ArchivedFold lists={archived} archiveM={unarchiveM} />}
        {unarchiveError && <p className="lp-error lp-error--nav" role="alert">{unarchiveError}</p>}
      </nav>
      <section className="lp-pane">
        {pick.open === SAVED_LIST
          ? <div className="desk-phone"><Shortlist only="saved" /></div>
          : readError
            ? <Failed error={readError.message} />
            : open
              ? <OpenList key={open.id} list={open} items={items.filter(i => i.list_id === open.id).sort(byPosition)} lists={lists} />
              : <p className="lp-empty">{pending ? 'Loading…' : 'No lists yet.'}</p>}
      </section>
    </div>
  )
}

function ArchivedFold({ lists, archiveM }: { lists: List[]; archiveM: ReturnType<typeof useArchiveList> }) {
  return (
    <details className="lp-arch">
      <summary>Archived · {lists.length}</summary>
      {lists.map(l => (
        <div key={l.id} className="lp-arch__row">
          <span>{l.name}</span>
          <button type="button" onClick={() => archiveM.mutate({ id: l.id, archived: false })}>Unarchive</button>
        </div>
      ))}
    </details>
  )
}

function NewList({ onDone }: { onDone: (name: string | null) => void }) {
  const addM = useAddList()
  const [name, setName] = useState('')
  const [kind, setKind] = useState<ListKind>('grocery')
  async function save() {
    const n = name.trim()
    if (!n) { onDone(null); return }
    if (await attempt(addM.mutateAsync({ name: n, kind }))) onDone(n)
  }
  return (
    <div className="lp-new">
      <input
        autoFocus value={name} placeholder="List name" aria-label="New list name"
        onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') void save(); if (e.key === 'Escape') onDone(null) }}
      />
      <select value={kind} aria-label="Kind" onChange={e => setKind(e.target.value as ListKind)}>
        {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span className="lp-new__row">
        <button type="button" onClick={() => void save()} disabled={addM.isPending}>Create</button>
        <button type="button" onClick={() => onDone(null)}>Cancel</button>
      </span>
    </div>
  )
}

function OpenList({ list, items, lists }: { list: List; items: ListItem[]; lists: List[] }) {
  const qc = useQueryClient()
  const addM = useAddItem()
  const bumpM = useBumpUsual()
  const toggleM = useSetItemDone()
  const completeM = useCompleteTask()
  const clearM = useClearDone()
  const moveM = useMoveLine()
  const updateM = useUpdateItem()
  const fieldsM = useSetItemFields()
  const reorderM = useReorderItems()
  const removeM = useRemoveItem()
  const renameM = useRenameList()
  const pinM = usePinList()
  const archiveM = useArchiveList()
  const toast = useToast()
  const usualsQ = useListUsuals(list.id)
  const aislesQ = useGroceryAisles()
  const known = aislesQ.data ?? []
  const dict = useAisleDict()
  const error = useWriteError(addM, toggleM, completeM, clearM, moveM, updateM, fieldsM, reorderM, removeM, renameM, pinM, archiveM)

  const addRef = useRef<HTMLInputElement>(null)
  const paneRef = useRef<HTMLDivElement>(null)
  const [draft, setDraft] = useState('')
  const [name, setName] = useState(list.name)
  const [drag, setDrag] = useState<{ id: string; over: string | null } | null>(null)
  // T2.2: the one open detail strip, by line id.
  const [strip, setStrip] = useState<string | null>(null)
  useEffect(() => { setName(list.name) }, [list.name])

  // D1: rows ticked within LINGER_MS, drawn open and struck through in their
  // place while the tick is already written; then `leaving` for LEAVE_MS
  // while the row collapses; then gone from the set, and the fold draws the
  // row under Done. The set is paint, never a column.
  const [linger, setLinger] = useState<Map<string, 'linger' | 'leaving'>>(() => new Map())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  useEffect(() => () => { for (const t of timers.current.values()) clearTimeout(t) }, [])
  const dropLinger = (id: string) => {
    const t = timers.current.get(id)
    if (t) clearTimeout(t)
    timers.current.delete(id)
    setLinger(m => { if (!m.has(id)) return m; const n = new Map(m); n.delete(id); return n })
  }
  function startLinger(id: string) {
    dropLinger(id)
    setLinger(m => new Map(m).set(id, 'linger'))
    timers.current.set(id, setTimeout(() => {
      // A write that failed has been rolled back by now: the row is open in
      // the cache and must not leave.
      const row = (qc.getQueryData<ListItem[]>(['list_items']) ?? []).find(r => r.id === id)
      if (!row || !isFinished(list.kind, row)) { dropLinger(id); return }
      // The collapse transitions `max-height`, which cannot start from
      // `none`: measure the row, hand the CSS its height through `--lp-h`,
      // force the reflow, then the class takes it to 0 (lists.css).
      const el = paneRef.current?.querySelector<HTMLElement>(`.lp-line[data-id="${CSS.escape(id)}"]`)
      if (el) { el.style.setProperty('--lp-h', `${el.getBoundingClientRect().height}px`); void el.offsetHeight }
      setLinger(m => new Map(m).set(id, 'leaving'))
      timers.current.set(id, setTimeout(() => dropLinger(id), LEAVE_MS))
    }, LINGER_MS))
  }
  const lingering = useMemo(() => new Set(linger.keys()), [linger])

  const today = berlinDay(new Date())
  const tasks = list.kind === 'task'
    ? foldTasks(items, { today, clearedAt: list.done_cleared_at ?? null, dayOf: berlinDay, lingering })
    : null
  const openN = items.filter(i => !isClosed(i, list.kind)).length

  /** The tick, for every kind (18 Oct, `lib/finished.ts`): a task goes
   *  through `tickTask` (D6); a grocery is ticked into the basket; both
   *  linger (D1) and then leave for their fold. Untick inside the linger
   *  cancels it. A purchase and a collection have no tick — `finishBuy` and
   *  `flag` below are their way out, with the same linger. */
  function tick(i: ListItem) {
    if (list.kind !== 'task') {
      if (i.done_at) { dropLinger(i.id); toggleM.mutate({ id: i.id, done: false }); return }
      toggleM.mutate({ id: i.id, done: true })
      if (list.kind !== 'grocery') return
      startLinger(i.id)
      toast({
        text: `"${i.body}" in the basket`,
        action: { label: 'Undo', onClick: () => { dropLinger(i.id); toggleM.mutate({ id: i.id, done: false }) } },
      })
      return
    }
    if (i.done_at) {
      dropLinger(i.id)
      tickTask({ done: toggleM, complete: completeM }, i, today)
      return
    }
    const { nextDueOn } = tickTask({ done: toggleM, complete: completeM }, i, today)
    startLinger(i.id)
    toast({
      text: nextDueOn ? `Done "${i.body}" · next ${shortDay(nextDueOn)}` : `Done "${i.body}"`,
      action: {
        label: 'Undo',
        onClick: () => {
          dropLinger(i.id)
          toggleM.mutate({ id: i.id, done: false })
          if (nextDueOn) toast({ text: 'Unticked · the next one stays' })
        },
      },
    })
  }

  /** A purchase's status from the row's select: `bought` is finished and
   *  lingers out to the Bought fold; anything else is a plain write, and a
   *  bought row moved back comes out of the fold at once. */
  function setStatus(i: ListItem, status: PurchaseStatus) {
    const was = i.status
    fieldsM.mutate({ id: i.id, patch: { status } })
    if (status !== 'bought') { dropLinger(i.id); return }
    startLinger(i.id)
    toast({
      text: `Marked "${i.body}" bought`,
      action: { label: 'Undo', onClick: () => { dropLinger(i.id); fieldsM.mutate({ id: i.id, patch: { status: was } }) } },
    })
  }

  /** The collection's flag: flagging lingers out to the fold; unflagging
   *  inside the linger cancels it and the row stays. */
  function flag(i: ListItem) {
    const prev = i.flagged_at
    const next = prev ? null : new Date().toISOString()
    fieldsM.mutate({ id: i.id, patch: { flagged_at: next } })
    if (!next) { dropLinger(i.id); return }
    startLinger(i.id)
    const word = list.flag_label || 'seen'
    toast({
      text: `Marked "${i.body}" ${word}`,
      action: { label: 'Undo', onClick: () => { dropLinger(i.id); fieldsM.mutate({ id: i.id, patch: { flagged_at: prev } }) } },
    })
  }

  /** L3's Clear basket, as the phone's GroceryList does it: the basket goes
   *  from the cache at once, the rows are deleted for real once the toast
   *  has run its course, and Undo just cancels the timer and refetches —
   *  nothing was removed yet, so there is nothing to put back. Groceries
   *  recur, which is why this deletes where the tasks' Clear only hides. */
  function clearBasket(basket: ListItem[]) {
    if (!basket.length) return
    const ids = new Set(basket.map(i => i.id))
    qc.setQueryData<ListItem[]>(['list_items'], prev => (prev ?? []).filter(i => !ids.has(i.id)))
    const timer = setTimeout(() => { for (const id of ids) removeM.mutate(id) }, 5000)
    toast({
      text: `Cleared ${ids.size} from the basket`,
      ms: 5000,
      action: { label: 'Undo', onClick: () => { clearTimeout(timer); void qc.invalidateQueries({ queryKey: ['list_items'] }) } },
    })
  }

  // D3: Clear hides every done line older than today; Undo puts the previous
  // cutoff back.
  function clearDone() {
    const was = list.done_cleared_at ?? null
    const n = tasks ? tasks.done.filter(g => g.key !== 'today').reduce((s, g) => s + g.lines.length, 0) : 0
    clearM.mutate({ listId: list.id, at: dayStart(today).toISOString() })
    toast({ text: `Cleared · ${n}`, action: { label: 'Undo', onClick: () => clearM.mutate({ listId: list.id, at: was }) } })
  }

  // D8: a line to another list. Undo is a move back, offered only when the
  // move lost nothing; otherwise the toast says what was dropped.
  function moveTo(i: ListItem, to: List) {
    const lost = droppedPhrase(dropped(list.kind, to.kind, i))
    moveM.mutate({ id: i.id, toListId: to.id, patch: movePatch(list.kind, to.kind, to.id) })
    setStrip(null)
    toast(lost
      ? { text: `Moved to ${to.name} · ${lost}` }
      : {
        text: `Moved to ${to.name}`,
        action: { label: 'Undo', onClick: () => moveM.mutate({ id: i.id, toListId: list.id, patch: movePatch(to.kind, list.kind, list.id) }) },
      })
  }

  async function add() {
    const text = draft.trim()
    if (!text) return
    let body = text
    const opts: {
      position: number; qty?: string | null; due_on?: string | null; section?: string | null
      repeat_every?: number | null; repeat_unit?: 'day' | 'week' | null
    } =
      // A checklist keeps adding at the end, under its last section; every
      // other kind puts the new row at the top (newPosition.ts).
      { position: list.kind === 'template' ? items.length : topPosition(items) }
    if (list.kind === 'grocery') {
      const p = parseQty(text); body = p.body; opts.qty = p.qty
    } else if (list.kind === 'task') {
      // "Get wine every week by Friday": the date and the repeat, in either order (T0.2).
      const p = parseTask(text)
      body = p.body; opts.due_on = p.due_on
      opts.repeat_every = p.repeat?.every ?? null; opts.repeat_unit = p.repeat?.unit ?? null
    } else if (list.kind === 'template') {
      opts.section = items[items.length - 1]?.section ?? null
    }
    if (!body) return
    if (await attempt(addM.mutateAsync({ listId: list.id, body, opts }))) {
      setDraft('')
      if (list.kind === 'grocery') {
        bumpM.mutate({ listId: list.id, body })
        if (dict && aisleOf(body, known, dict) === 'Other' && needsLookup(body, known, dict)) void requestAisleLookup(body, qc)
      }
      addRef.current?.focus()
    }
  }

  function dropOn(targetId: string) {
    const d = drag
    setDrag(null)
    if (!d || d.id === targetId) return
    const ids = items.map(i => i.id)
    const down = ids.indexOf(d.id) < ids.indexOf(targetId)
    const order = ids.filter(id => id !== d.id)
    // Dropped on a line below, it lands after it; on one above, before it.
    order.splice(order.indexOf(targetId) + (down ? 1 : 0), 0, d.id)
    reorderM.mutate(order)
  }

  // Alt+↑/↓ (a3-29): the line swaps with its neighbour as drawn — in its
  // aisle, section or subtasks (`groups`, filled as the body is built below).
  // React moves the line's node to reorder it, which takes focus off the
  // field the key was pressed in, so the field is found again by its line
  // and its class once the optimistic order has drawn.
  const groups: string[][] = []
  // Once only, on the first render that draws a different order: a later
  // render must not pull focus back from wherever Doug has gone since.
  const orderKey = items.map(i => i.id).join()
  const refocus = useRef<{ id: string; cls: string; was: string } | null>(null)
  useLayoutEffect(() => {
    const r = refocus.current
    if (!r || r.was === orderKey) return
    refocus.current = null
    const line = document.querySelector<HTMLElement>(`.lp-line[data-id="${CSS.escape(r.id)}"]`)
    ;(r.cls === 'lp-line' ? line : line?.querySelector<HTMLElement>(`.${r.cls}`))?.focus()
  }, [orderKey])
  function step(id: string, dir: -1 | 1, cls: string) {
    const order = stepIn(items.map(i => i.id), groups, id, dir)
    if (!order) return
    refocus.current = { id, cls, was: orderKey }
    reorderM.mutate(order)
  }

  /** D11: ↑/↓ between the rows as drawn, Done groups included when open. */
  function focusRow(fromId: string, dir: -1 | 1) {
    const rows = [...(paneRef.current?.querySelectorAll<HTMLElement>('.lp-line[tabindex]') ?? [])]
    const at = rows.findIndex(r => r.dataset.id === fromId)
    rows[at + dir]?.focus()
  }

  // `child` is where the line is drawn, not whether it has a parent: an open
  // subtask of a folded task stands at the top (a3-14), unindented.
  const line = (i: ListItem, child = false) => (
    <Line
      key={i.id} list={list} item={i} child={child} dragging={drag?.id === i.id} over={drag?.over === i.id && drag.id !== i.id}
      pinned={list.kind === 'task' && pinnedByDue(i, today)}
      leaving={linger.get(i.id) === 'leaving'}
      today={today}
      stripOpen={strip === i.id}
      onStrip={open => setStrip(open ? i.id : null)}
      onDragStart={() => setDrag({ id: i.id, over: null })}
      onDragOver={() => { if (drag && drag.over !== i.id) setDrag({ ...drag, over: i.id }) }}
      onDrop={() => dropOn(i.id)}
      onDragEnd={() => setDrag(null)}
      onToggle={() => tick(i)}
      onBody={body => updateM.mutate({ id: i.id, patch: { body } })}
      onQty={qty => updateM.mutate({ id: i.id, patch: { qty } })}
      onFields={patch => fieldsM.mutate({ id: i.id, patch })}
      onStatus={status => setStatus(i, status)}
      onFlag={() => flag(i)}
      onRemove={() => toastRemovedLine(i, items.filter(c => c.parent_id === i.id), removeM.mutate)}
      onMove={to => moveTo(i, to)}
      others={lists.filter(l => l.id !== list.id && !l.archived_at)}
      onStep={(dir, cls) => step(i.id, dir, cls)}
      onFocusRow={dir => focusRow(i.id, dir)}
      focusAdd={() => addRef.current?.focus()}
    />
  )

  // 18 Oct: the finished rows of a grocery, purchase or collection list leave
  // the open rows for a fold at the bottom (`lib/finished.ts`), as a task's
  // do. The fold's rows are drawn after `body`, below. A lingering row is
  // still among the open ones, in its place.
  const fin = list.kind === 'task' || list.kind === 'template' ? null : foldFinished(list.kind, items, lingering)
  const openRows = fin ? fin.open : items

  let body: JSX.Element[]
  if (list.kind === 'grocery') {
    const byAisle: Record<string, ListItem[]> = {}
    for (const i of openRows) (byAisle[aisleOf(i.body, known, dict ?? [])] ??= []).push(i)
    const aisles = AISLES.filter(a => byAisle[a]?.length)
    groups.push(...aisles.map(a => byAisle[a].map(i => i.id)))
    body = aisles.map(a => (
      <div key={a}><h3 className="lp-sect">{a}</h3>{byAisle[a].map(i => line(i))}</div>
    ))
  } else if (list.kind === 'template') {
    const secs = [...new Set(items.map(i => i.section ?? ''))]
    groups.push(...secs.map(s => items.filter(i => (i.section ?? '') === s).map(i => i.id)))
    body = secs.map(s => (
      <div key={s || '_'}><h3 className="lp-sect">{s || 'Steps'}</h3>{items.filter(i => (i.section ?? '') === s).map(i => line(i))}</div>
    ))
  } else if (tasks) {
    groups.push(
      tasks.top.map(t => t.item.id), ...tasks.top.map(t => t.children.map(c => c.id)),
      ...tasks.done.map(g => g.lines.map(i => i.id)),
    )
    body = tasks.top.flatMap(t => [line(t.item), ...t.children.map(c => line(c, true))])
  } else {
    groups.push(openRows.map(i => i.id))
    body = openRows.map(i => line(i))
  }
  if (fin?.finished.length) groups.push(fin.finished.map(i => i.id))

  const usuals = list.kind === 'grocery' ? usualsToOffer(usualsQ.data ?? [], items, MAX_USUALS) : []

  // As the phone's `ListMenu`: archiving takes the list off the nav at once,
  // so it gets the same Undo a removal does (a3-13), which also opens it again.
  const archive = () => {
    archiveM.mutate({ id: list.id, archived: true })
    toast({
      text: `Archived "${list.name}"`,
      action: { label: 'Undo', onClick: () => { archiveM.mutate({ id: list.id, archived: false }); go('lists', list.id) } },
    })
  }

  const clearable = !!tasks && canClear(items, list.done_cleared_at ?? null, today, berlinDay)

  return (
    <div className="lp-list" ref={paneRef}>
      <header className="lp-head">
        <input
          className="lp-head__name" value={name} aria-label="List name" spellCheck={false}
          onChange={e => setName(e.target.value)}
          onBlur={() => {
            const n = name.trim()
            if (!n) setName(list.name)
            else if (n !== list.name) renameM.mutate({ id: list.id, name: n })
          }}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
        />
        <span className="lp-head__meta">
          {/* The same rule as the nav: "Purchases · 15 open" under a list
              called Purchases said the name twice (a4-19). */}
          {kindAddsToName(list.name, KIND_LABEL[list.kind]) && `${KIND_LABEL[list.kind]} · `}{openN} open
          {list.kind === 'template' ? ' · a checklist is a template you run, so its lines stay' : ''}
        </span>
        <span className="lp-head__acts">
          <button type="button" aria-pressed={list.pinned} onClick={() => pinM.mutate({ id: list.id, pinned: !list.pinned })}>
            {list.pinned ? 'Unpin' : 'Pin'}
          </button>
          <button type="button" onClick={archive}>Archive</button>
        </span>
      </header>

      {error && <p className="lp-error" role="alert">{error}</p>}

      <div className="lp-add">
        <input
          ref={addRef} value={draft} placeholder={PLACEHOLDER[list.kind]} aria-label="Add" autoComplete="off"
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void add() } }}
        />
        <button type="button" disabled={addM.isPending || !draft.trim()} onClick={() => void add()}>Add</button>
      </div>

      {usuals.length > 0 && (
        <div className="lp-usuals">
          {usuals.map(u => (
            <button
              key={u.body} type="button"
              onClick={() => {
                addM.mutate({ listId: list.id, body: u.body, opts: { position: topPosition(items) } })
                bumpM.mutate({ listId: list.id, body: u.body })
              }}
            >{u.body}</button>
          ))}
        </div>
      )}

      {body.length ? body : <p className="lp-empty">{tasks?.doneCount || fin?.finished.length ? 'Nothing open.' : 'Empty.'}</p>}
      {fin && fin.finished.length > 0 && (
        // The fold for a grocery's basket (open by default: you look at it
        // while shopping), a purchase list's Bought and a collection's Seen
        // (closed: history). Clear basket sits in the summary like the tasks'
        // Clear, its click stopped before the summary would toggle on it.
        <details className={`lp-done${list.kind === 'grocery' ? ' lp-basket' : ''}`} open={list.kind === 'grocery' || undefined}>
          <summary className="lp-sect">
            <span>{finishedHead(list.kind, list, fin.finished.length)}</span>
            {list.kind === 'grocery' && (
              <button
                type="button" className="lp-clear lp-clearbasket" title="Remove everything in the basket"
                onClick={e => { e.preventDefault(); e.stopPropagation(); clearBasket(fin.finished) }}
              >Clear basket</button>
            )}
          </summary>
          {fin.finished.map(i => line(i))}
        </details>
      )}
      {tasks && tasks.doneCount > 0 && (
        // The phone's "Done · N" fold (a3-14), by day and newest first (D3).
        // Clear sits inside the summary so it reads as the fold's own; its
        // click is stopped before the summary would toggle on it.
        <details className="lp-done">
          <summary className="lp-sect">
            <span>Done · {tasks.doneCount}</span>
            {clearable && (
              <button
                type="button" className="lp-clear" title="Hide everything done before today"
                onClick={e => { e.preventDefault(); e.stopPropagation(); clearDone() }}
              >Clear</button>
            )}
          </summary>
          {tasks.done.map(g => (
            <div key={g.key} className="lp-done__group">
              <h4 className="lp-sect lp-sect--day">{g.label}</h4>
              {g.lines.map(i => line(i))}
            </div>
          ))}
        </details>
      )}
    </div>
  )
}

interface LineProps {
  list: List; item: ListItem; child: boolean; dragging: boolean; over: boolean
  /** D4: pinned above the manual order by its due date — not a drag source or target. */
  pinned: boolean
  /** D1: collapsing on its way out. */
  leaving: boolean
  today: string
  stripOpen: boolean; onStrip: (open: boolean) => void
  onDragStart: () => void; onDragOver: () => void; onDrop: () => void; onDragEnd: () => void
  onToggle: () => void; onBody: (body: string) => void; onQty: (qty: string | null) => void
  onFields: (patch: Parameters<ReturnType<typeof useSetItemFields>['mutate']>[0]['patch']) => void
  /** A purchase's status and a collection's flag go through the screen, which
   *  lingers the row out to its fold when the change finishes it (18 Oct). */
  onStatus: (status: PurchaseStatus) => void; onFlag: () => void
  onRemove: () => void; focusAdd: () => void
  /** D8: the other active lists, and the move. */
  others: List[]; onMove: (to: List) => void
  /** Alt+↑/↓; `cls` names the field the key was pressed in, to focus again. */
  onStep: (dir: -1 | 1, cls: string) => void
  /** D11: ↑/↓ on the row. */
  onFocusRow: (dir: -1 | 1) => void
}

const isField = (el: EventTarget | null) =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement

function Line(p: LineProps) {
  const { list, item } = p
  const [body, setBody] = useState(item.body)
  const [qty, setQty] = useState(item.qty ?? '')
  const [price, setPrice] = useState(euros(item.price_cents))
  const rowRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setBody(item.body) }, [item.body])
  useEffect(() => { setQty(item.qty ?? '') }, [item.qty])
  useEffect(() => { setPrice(euros(item.price_cents)) }, [item.price_cents])

  const isTask = list.kind === 'task'
  const due = isTask && item.due_on ? dueLabel(item.due_on, p.today) : null
  const every = item.repeat_every ?? null, unit = item.repeat_unit ?? null
  const repeat = isTask && every && unit ? { every, unit } : null
  const note = isTask ? (item.note ?? null) : null

  const cls = ['lp-line', item.done_at ? 'is-done' : '', p.child ? 'is-child' : '',
    p.dragging ? 'is-dragging' : '', p.over ? 'is-over' : '', p.leaving ? 'is-leaving' : '',
    note ? 'has-note' : '', p.stripOpen ? 'is-open' : ''].filter(Boolean).join(' ')

  /** Escape in the title: the words go back and the write never happens; the
   *  blur that follows sees `body === item.body` and writes nothing. */
  const revert = useRef(false)

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement
    const inField = isField(target)
    const inStrip = inField && !!target.closest('.lp-strip')
    const action = deskKeyAction({ key: e.key, altKey: e.altKey, metaKey: e.metaKey, ctrlKey: e.ctrlKey, inField, inStrip })
    if (!action) return
    // A field's own keys stay its own except where the table says so; a
    // row's keys never scroll the pane or go Back.
    const fieldCls = [...target.classList].reverse().find(c => c.startsWith('lp-')) ?? 'lp-line'
    switch (action) {
      case 'stepUp': case 'stepDown':
        // Alt+arrow in a field is the caret's (start or end of the line on a
        // Mac); here it is the line's, as the grip's title says.
        e.preventDefault(); p.onStep(action === 'stepUp' ? -1 : 1, fieldCls); return
      case 'focusPrev': case 'focusNext':
        e.preventDefault(); p.onFocusRow(action === 'focusPrev' ? -1 : 1); return
      case 'tick':
        if (list.kind === 'collection') return
        e.preventDefault(); p.onToggle(); return
      case 'edit':
        e.preventDefault(); bodyRef.current?.focus(); bodyRef.current?.select(); return
      case 'save':
        e.preventDefault()
        target.blur()
        if (isTask) rowRef.current?.focus(); else p.focusAdd()
        return
      case 'revert':
        e.preventDefault()
        if (target === bodyRef.current) { revert.current = true; setBody(item.body) }
        target.blur()
        rowRef.current?.focus()
        return
      case 'saveStrip':
        e.preventDefault(); target.blur(); rowRef.current?.focus(); return
      case 'remove':
        e.preventDefault(); p.onRemove(); return
      case 'openStrip':
        if (!isTask) return
        e.preventDefault(); p.onStrip(true); return
      case 'closeStrip':
        if (p.stripOpen) { e.preventDefault(); p.onStrip(false) }
        return
      default: return
    }
  }

  return (
    <>
      <div
        ref={rowRef}
        className={cls} data-id={item.id} tabIndex={0}
        onKeyDown={onKeyDown}
        onDragOver={e => { if (p.pinned) return; e.preventDefault(); p.onDragOver() }}
        onDrop={e => { if (p.pinned) return; e.preventDefault(); p.onDrop() }}
      >
        <span
          className="lp-grip" draggable={!p.pinned}
          aria-label={p.pinned ? `"${item.body}" is sorted by its due date` : `Drag "${item.body}"`}
          title={p.pinned ? 'Sorted by its due date' : 'Drag to reorder, or Alt+↑/↓ from the line'}
          onDragStart={e => { if (p.pinned) { e.preventDefault(); return } e.dataTransfer.effectAllowed = 'move'; p.onDragStart() }}
          onDragEnd={p.onDragEnd}
        >{p.pinned ? '' : '⋮⋮'}</span>
        {list.kind === 'collection'
          ? <span />
          : <input
              type="checkbox" className="lp-tick" checked={!!item.done_at} onChange={p.onToggle}
              aria-label={item.done_at ? `Mark "${item.body}" not done` : `Mark "${item.body}" done`}
            />}
        <input
          ref={bodyRef}
          className="lp-body" value={body} spellCheck={false} aria-label="Line"
          onChange={e => setBody(e.target.value)}
          onBlur={() => {
            if (revert.current) { revert.current = false; return }
            const b = body.trim()
            if (!b) setBody(item.body)
            else if (b !== item.body) p.onBody(b)
          }}
        />
        {note && <span className="lp-note" title={note}>{note}</span>}
        {list.kind === 'grocery' && (
          <input
            className="lp-cell lp-qty" value={qty} aria-label="Quantity"
            onChange={e => setQty(e.target.value)}
            onBlur={() => { const q = qty.trim() || null; if (q !== item.qty) p.onQty(q) }}
          />
        )}
        {repeat && (
          <span className="lp-repeat" role="img" aria-label={`Repeats ${repeatLabel(repeat)}`} title={`Repeats ${repeatLabel(repeat)}`}>
            <Repeat size={14} aria-hidden="true" />
          </span>
        )}
        {due && (
          // D5: the chip only when there is a date; the tap opens the strip
          // where the date is edited.
          <button type="button" className={`lp-chip is-${due.tone}`} onClick={() => p.onStrip(true)}
            aria-label={`Due ${due.text}, change`}>{due.text}</button>
        )}
        {list.kind === 'buy' && (
          <>
            {/* a3-30: a purchase with no status drew a blank select, a bare
                chevron. No status *is* considering — `purchaseStatusOf`, the
                rule the phone groups by and the index counts by (a2-01) — so
                the menu says so, rather than show a third, empty state only
                the desk had. Picking "considering" on such a row writes
                nothing: it already is. */}
            <select
              className="lp-cell lp-status" value={purchaseStatusOf(item)} aria-label="Status"
              onChange={e => p.onStatus(e.target.value as PurchaseStatus)}
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {/* The currency's symbol until there is a price: an empty box read
                as nothing, not as the place a price goes (a3-30). A priced item
                shows its own currency's, an unpriced one the current region's
                (C2.3), and the first price an item gets is written in that
                currency; a price it already had keeps the one it was set in. */}
            <input
              className="lp-cell lp-price" value={price} aria-label="Price" inputMode="decimal"
              placeholder={priceSymbol(item.price_cents == null ? hereCurrency() : item.currency)}
              onChange={e => setPrice(e.target.value)}
              onBlur={() => {
                const c = toCents(price)
                if (c !== item.price_cents) p.onFields(item.price_cents == null && c != null ? { price_cents: c, currency: hereCurrency() } : { price_cents: c })
                else setPrice(euros(item.price_cents))
              }}
            />
          </>
        )}
        {list.kind === 'collection' && (
          <button
            type="button" className="lp-flag" aria-pressed={!!item.flagged_at}
            onClick={p.onFlag}
          >{item.flagged_at ? `✓ ${list.flag_label || 'seen'}` : list.flag_label || 'seen'}</button>
        )}
        {isTask
          // T2.2: a task row opens its strip; × lives in the strip's action row.
          ? <button type="button" className="lp-more" aria-expanded={p.stripOpen} aria-label={`Details of "${item.body}"`}
              onClick={() => p.onStrip(!p.stripOpen)}>›</button>
          : <button type="button" className="lp-x" aria-label={`Remove "${item.body}"`} onClick={p.onRemove}>×</button>}
      </div>
      {isTask && p.stripOpen && (
        <Strip item={item} repeat={repeat} others={p.others} onFields={p.onFields} onMove={p.onMove} onRemove={p.onRemove}
          onClose={() => { p.onStrip(false); rowRef.current?.focus() }} onKeyDown={onKeyDown} />
      )}
    </>
  )
}

/** T2.2: the detail strip under a task row — note, due, repeat, move, remove.
 *  Fields save on blur, one write each (the desk's inline convention); the
 *  selects write on change. Escape closes it from anywhere inside. */
function Strip({ item, repeat, others, onFields, onMove, onRemove, onClose, onKeyDown }: {
  item: ListItem; repeat: { every: number; unit: 'day' | 'week' } | null; others: List[]
  onFields: LineProps['onFields']; onMove: (to: List) => void; onRemove: () => void; onClose: () => void
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void
}) {
  const [note, setNote] = useState(item.note ?? '')
  useEffect(() => { setNote(item.note ?? '') }, [item.note])
  const mode = !repeat ? 'never' : repeat.every === 1 && repeat.unit === 'day' ? 'daily' : repeat.every === 1 && repeat.unit === 'week' ? 'weekly' : 'every'
  const [every, setEvery] = useState(repeat?.every ?? 2)
  const [unit, setUnit] = useState<'day' | 'week'>(repeat?.unit ?? 'week')
  const noteRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { noteRef.current?.focus() }, [])

  const setRepeat = (m: string) => {
    if (m === 'never') onFields({ repeat_every: null, repeat_unit: null })
    else if (m === 'daily') onFields({ repeat_every: 1, repeat_unit: 'day' })
    else if (m === 'weekly') onFields({ repeat_every: 1, repeat_unit: 'week' })
    else onFields({ repeat_every: every, repeat_unit: unit })
  }

  return (
    <div className="lp-strip" data-id={item.id}
      onKeyDown={e => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return }
        onKeyDown(e)
      }}>
      <label className="lp-strip__field lp-strip__field--note">
        <span>Note</span>
        <textarea
          ref={noteRef} className="lp-strip__note" value={note} rows={2} placeholder="A line or two"
          onChange={e => setNote(e.target.value)}
          onBlur={() => { const n = note.trim() || null; if (n !== (item.note ?? null)) onFields({ note: n }) }}
        />
      </label>
      <label className="lp-strip__field">
        <span>Due</span>
        <span className="lp-strip__row">
          <input type="date" className="lp-strip__due" value={item.due_on ?? ''} aria-label="Due date"
            onChange={e => onFields({ due_on: e.target.value || null })} />
          {item.due_on && <button type="button" className="lp-strip__none" onClick={() => onFields({ due_on: null })}>None</button>}
        </span>
      </label>
      <label className="lp-strip__field">
        <span>Repeat</span>
        <span className="lp-strip__row">
          <select className="lp-strip__repeat" value={mode} aria-label="Repeat" onChange={e => setRepeat(e.target.value)}>
            <option value="never">Never</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="every">Every…</option>
          </select>
          {mode === 'every' && (
            <>
              <input type="number" min={1} max={365} className="lp-strip__n" value={every} aria-label="Every how many"
                onChange={e => setEvery(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                onBlur={() => { if (every !== repeat?.every) onFields({ repeat_every: every, repeat_unit: unit }) }} />
              <select className="lp-strip__unit" value={unit} aria-label="Days or weeks"
                onChange={e => { const u = e.target.value as 'day' | 'week'; setUnit(u); onFields({ repeat_every: every, repeat_unit: u }) }}>
                <option value="day">days</option>
                <option value="week">weeks</option>
              </select>
            </>
          )}
        </span>
      </label>
      <span className="lp-strip__acts">
        <select className="lp-strip__move" value="" aria-label="Move to another list"
          onChange={e => { const to = others.find(l => l.id === e.target.value); if (to) onMove(to) }}>
          <option value="" disabled>Move to…</option>
          {others.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <button type="button" className="lp-strip__remove" onClick={onRemove}>Remove</button>
        <button type="button" className="lp-strip__close" onClick={onClose} aria-label="Close">Close</button>
      </span>
    </div>
  )
}
